import {Button, DropdownMenu} from '@wandb/components';
import React, {useState} from 'react';

import {
  SlurmCommandState,
  type SlurmLatestCommandQuery,
} from '../../generated/graphql';
import {useQuery} from '../../state/graphql/apolloWithAbort';
import {SplitButton} from '../SplitButton';
import {
  type SlurmConfirmVariant,
  SlurmJobConfirmModal,
} from './SlurmJobConfirmModal';
import {SLURM_LATEST_COMMAND_QUERY} from './slurmJobQueries.query';

type SlurmRunActionsProps = {
  entityName: string;
  projectName: string;
  runName: string;
  jobId: string;
  isRunLive: boolean;
  showStopRun: boolean;
  onStopRun: () => void;
};

export function SlurmRunActions({
  entityName,
  projectName,
  runName,
  jobId,
  isRunLive,
  showStopRun,
  onStopRun,
}: SlurmRunActionsProps) {
  const [confirmVariant, setConfirmVariant] =
    useState<SlurmConfirmVariant | null>(null);
  const [isRefreshingLatestCommand, setIsRefreshingLatestCommand] =
    useState(false);
  const {data: latestCommandData, refetch: refetchLatestCommand} =
    useQuery<SlurmLatestCommandQuery>(SLURM_LATEST_COMMAND_QUERY, {
      variables: {entityName, projectName, runName},
      skip: !runName,
      fetchPolicy: 'cache-and-network',
    });
  const {latestCommand} = latestCommandData?.project?.run?.slurmControl ?? {};

  const hasActiveCommand =
    latestCommand?.state != null &&
    [SlurmCommandState.Pending, SlurmCommandState.Leased].includes(
      latestCommand?.state
    );
  const isCommandDisabled = isRefreshingLatestCommand || hasActiveCommand;

  const refreshLatestCommand = async () => {
    setIsRefreshingLatestCommand(true);
    try {
      await refetchLatestCommand();
    } catch (error) {
      console.warn('Failed to refresh latest Slurm command', error);
    } finally {
      setIsRefreshingLatestCommand(false);
    }
  };

  return (
    <>
      <SlurmJobConfirmModal
        isOpen={confirmVariant != null}
        variant={confirmVariant ?? 'cancel'}
        jobId={jobId}
        entityName={entityName}
        projectName={projectName}
        runName={runName}
        isRunLive={isRunLive}
        onClose={() => setConfirmVariant(null)}
        onSuccess={refreshLatestCommand}
      />
      <span
        className="inline-flex"
        title={
          isCommandDisabled
            ? 'A Slurm command is already in progress.'
            : undefined
        }>
        {isRunLive ? (
          <SplitButton
            startIcon="do-not-prohibited"
            isDisabled={isCommandDisabled}
            isMenuDisabled={isCommandDisabled}
            onPress={() => setConfirmVariant('cancel')}
            menuContent={
              <DropdownMenu.Content placement="bottom end">
                <DropdownMenu.Item
                  onAction={() => setConfirmVariant('requeue')}>
                  <DropdownMenu.ItemDecorationLeft
                    iconProps={{icon: 'reset'}}
                  />
                  <DropdownMenu.ItemLabel>
                    Requeue Slurm job
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                {showStopRun && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item onAction={onStopRun}>
                      <DropdownMenu.ItemDecorationLeft
                        iconProps={{icon: 'stop'}}
                      />
                      <DropdownMenu.ItemLabel>Stop run</DropdownMenu.ItemLabel>
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            }>
            Cancel Slurm job
          </SplitButton>
        ) : (
          // Stop-run is only available for live runs, so this non-live branch
          // intentionally renders the standalone Slurm requeue action.
          <Button
            size="md"
            variant="secondary"
            startIcon="reset"
            isDisabled={isCommandDisabled}
            onPress={() => setConfirmVariant('requeue')}>
            Requeue Slurm job
          </Button>
        )}
      </span>
    </>
  );
}
